<template>
  <BaseModule
    class="container amenities-slider"
    data-module="amenities-slider"
    v-bind="$props"
  >
    <div class="content">
      <header
        v-if="title || subtitle || text"
        class="header base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
          :style="{ fontWeight: titleWeight }"
        >
          {{ title }}
        </component>
        <h3
          v-if="subtitle"
          class="sub"
        >
          {{ subtitle }}
        </h3>
        <div
          v-if="text"
          class="txt"
          v-html="text"
        ></div>
      </header>

      <!-- Slider main container -->
      <div
        ref="swiper"
        class="swiper"
      >
        <Loader v-if="loading && allSlides.length === 0" />

        <!-- Additional required wrapper -->
        <div
          v-show="allSlides.length > 0"
          class="swiper-wrapper"
        >
          <div
            v-for="(slide, idx) in allSlides"
            :key="slide.id || idx"
            class="swiper-slide"
            :class="{ 'swiper-slide-active': !hasMultipleSlides }"
          >
            <div class="img-wrapper">
              <img
                :src="
                  isMobile && slide.differentMobileBackgroundImage
                    ? slide.imageMobile
                    : slide.image
                "
                :alt="
                  isMobile &&
                  slide.differentMobileBackgroundImage &&
                  slide.altTextMobile
                    ? slide.altTextMobile
                    : slide.altText
                "
              />
            </div>
          </div>
        </div>
        <div
          v-if="hasMultipleSlides"
          class="swiper-button-wrapper"
        >
          <div :class="`swiper-button-prev btn-prev-${instanceId}`"></div>
          <div :class="`swiper-button-next btn-next-${instanceId}`"></div>
        </div>
      </div>

      <div
        v-if="currentBottomText"
        class="txt txt-bottom"
        v-html="currentBottomText"
      ></div>
    </div>
  </BaseModule>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Swiper from 'swiper';
import { Autoplay, Navigation } from 'swiper/modules';
import debounce from 'lodash/debounce';

import getApiHotels from '@/mixins/getApiHotels';
import Loader from '@/components/Loader.vue';
import showButton from '@/mixins/showButton';
import base from '@/mixins/base';
import IconArrow from '@/components/icons/IconArrow.vue';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'AmenitiesSlider',
  components: {
    BaseModule,
    Loader,
    IconArrow,
  },
  mixins: [getApiHotels, showButton, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    hotelId: {
      type: Number,
      default: null,
    },
    useHotelGallery: {
      type: Boolean,
      default: false,
    },
    useBottomText: {
      type: Boolean,
      default: false,
    },
    text: {
      type: String,
      default: '',
    },
    bottomText: {
      type: String,
      default: '',
    },
    slides: {
      type: Array,
      default() {
        return [];
      },
    },
    swiperAutoplay: {
      type: Boolean,
      default: false,
    },
    swiperAutoplaySpeed: {
      type: Number,
      default: 10,
    },
  },
  data() {
    return {
      mobileMaxWidth: 1020,
      innerWidth: null,
      swiper: null,
      isMobile: false,
      loading: false,
      currentSlideIndex: 0,
    };
  },
  computed: {
    selectedHotel() {
      return this.hotelId
        ? this.hotels.find((hotel) => hotel.Codigo === this.hotelId)
        : null;
    },
    allSlides() {
      const slides = [...this.slides];
      const hotelImages = this.selectedHotel?.Imagens;

      if (this.useHotelGallery && hotelImages) {
        if (hotelImages.ImagemPrincipal) {
          slides.push({
            image: hotelImages.ImagemPrincipal,
            imageMobile:
              hotelImages.ImagemPrincipalMobile || hotelImages.ImagemPrincipal,
          });
        }

        if (hotelImages.Galeria) {
          hotelImages.Galeria.forEach((image) => {
            slides.push({
              image,
              imageMobile: image,
            });
          });
        }
      }

      return slides;
    },
    hasMultipleSlides() {
      return this.allSlides.length > 1;
    },
    allAmenities() {
      const amenities = [];

      if (this.selectedHotel && this.selectedHotel.Amenidades) {
        this.selectedHotel.Amenidades.forEach((amenitie) => {
          amenities.push({ name: amenitie.Nome });
        });
      }
      return amenities;
    },
    swiperAutoplaySettings() {
      if (!this.editing && this.swiperAutoplay && this.swiperAutoplaySpeed) {
        return {
          delay: this.swiperAutoplaySpeed * 1000,
        };
      }
      return false;
    },
    editing() {
      return typeof this.$parent === 'undefined';
    },
    currentBottomText() {
      const currentSlide = this.allSlides[this.currentSlideIndex];

      return this.useBottomText
        ? this.bottomText
        : currentSlide?.bottomText || '';
    },
    ...mapState(['currentEditableItem', 'activeInstance']),
    ...mapGetters(['hasLetsbookIntegration']),
  },
  watch: {
    allSlides() {
      if (this.swiper) {
        this.swiperDestroy();
      }
      if (this.hasMultipleSlides) {
        this.swiperInit();
      }
      if (this.currentSlideIndex >= this.allSlides.length) {
        this.currentSlideIndex = 0;
      }
    },
    currentEditableItem() {
      this.slideToActivatedItem();
    },
    useHotelGallery(checked) {
      if (checked) {
        this.$store.commit('SET_ACTIVE_EDITABLE_OBJECT_DATA', {
          path: 'data.useBottomText',
          value: true,
        });
      }
    },
  },
  async mounted() {
    if (this.hasMultipleSlides) {
      this.swiperInit();
    }
    this.onResize();
    window.addEventListener('resize', debounce(this.onResize, 150));

    if (this.hasLetsbookIntegration) {
      this.loading = true;
      try {
        await this.getApiHotels();
      } catch (err) {
        console.warn(err);
      }
      this.loading = false;
    }
  },
  methods: {
    slideToActivatedItem() {
      if (
        this.activeInstance === this.instanceId &&
        this.currentEditableItem != null
      ) {
        if (this.currentEditableItem.includes('slides')) {
          const itemPathIndex = Number(
            this.currentEditableItem.split('.').pop(),
          );
          if (this.swiper && itemPathIndex < this.swiper.slides.length) {
            this.swiper.slideToLoop(itemPathIndex);
          }
        }
      }
    },
    onResize() {
      const isMobile = window.innerWidth < this.mobileMaxWidth;
      this.innerWidth = window.innerWidth;

      // If "device" changed...
      if (isMobile !== this.isMobile) {
        this.isMobile = isMobile;
      }
    },
    swiperInit() {
      this.$nextTick(() => {
        this.swiper = new Swiper(this.$refs.swiper, {
          modules: [Autoplay, Navigation],
          autoplay: this.swiperAutoplaySettings,
          observer: true,
          centeredSlides: true,
          speed: 700,
          slidesPerView: 1.2,
          allowTouchMove: false,
          navigation: {
            nextEl: `.btn-next-${this.instanceId}`,
            prevEl: `.btn-prev-${this.instanceId}`,
          },
        });
        this.swiper.on('slideChange', () => {
          this.currentSlideIndex = this.swiper.realIndex;
        });

        this.slideToActivatedItem();
      });
    },
    swiperDestroy() {
      this.swiper.destroy();
      this.swiper = null;
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'text',
                type: 'Html',
                label: 'Texto de descrição',
                wrapWithParagraph: false,
              },
            ],
          },
          {
            name: 'slides',
            label: 'Slides',
            fields: [
              {
                name: 'slides',
                type: 'List',
                itemLabel: 'Slide {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar slide',
                fields: [
                  {
                    name: 'image',
                    type: 'ImageFile',
                    maxWidth: 960,
                    label: 'Imagem',
                    helpText: 'Dimensões recomendadas: 1200x450',
                  },
                  {
                    name: 'altText',
                    type: 'ShortText',
                    label: 'Texto alternativo',
                  },
                  {
                    name: 'bottomText',
                    type: 'Html',
                    label: 'Texto de apoio',
                    wrapWithParagraph: false,
                    isEnabled({ useBottomText, useHotelGallery }) {
                      return !useBottomText && !useHotelGallery;
                    },
                  },
                  {
                    name: 'differentMobileBackgroundImage',
                    type: 'Checkbox',
                    label: 'Usar uma imagem diferente para mobile',
                  },
                  {
                    name: 'imageMobile',
                    type: 'ImageFile',
                    maxWidth: 960,
                    label: 'Imagem mobile',
                    helpText: 'Dimensões recomendadas: 312x450',
                    isEnabled() {
                      return this.differentMobileBackgroundImage;
                    },
                  },
                  {
                    name: 'altTextMobile',
                    type: 'ShortText',
                    label: 'Texto alternativo mobile',
                    isEnabled() {
                      return this.differentMobileBackgroundImage;
                    },
                  },
                ],
              },
            ],
          },
          {
            name: 'config',
            label: 'Configurações',
            fields: [
              {
                name: 'useHotelGallery',
                type: 'Checkbox',
                label: 'Mostrar galeria do hotel',
                isEnabled({ hasLetsbookIntegration }) {
                  return hasLetsbookIntegration;
                },
              },
              {
                name: 'hotelId',
                type: 'VueSelect',
                label: 'Selecione o hotel',
                isEnabled({ useHotelGallery }) {
                  return useHotelGallery;
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Selecione o hotel para integração',
                  },
                },
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Codigo,
                    }));

                  // Are hotels already loaded?
                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  // THIS IS PURE MAGIC
                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
              },
              {
                name: 'useBottomText',
                type: 'Checkbox',
                label: 'Adicionar texto de apoio fixo abaixo das imagens',
                helpText:
                  'Ao ativar, os textos de apoio nos slides serão desativados e permanecerá apenas o texto fixo.',
                disabled({ useHotelGallery }) {
                  return useHotelGallery;
                },
              },
              {
                name: 'bottomText',
                type: 'Html',
                label: 'Texto de apoio',
                wrapWithParagraph: false,
                isEnabled({ useBottomText }) {
                  return useBottomText;
                },
              },
              {
                name: 'swiperAutoplay',
                type: 'Checkbox',
                label: 'Habilitar transição automática das imagens (autoplay)',
                isEnabled({ useFixedText }) {
                  return !useFixedText;
                },
              },
              {
                name: 'swiperAutoplaySpeed',
                type: 'Range',
                label: 'Velocidade da transição das imagens',
                min: 1,
                max: 30,
                step: 1,
                format: {
                  suffix: 's',
                },
                isEnabled({ swiperAutoplay, useFixedText }) {
                  return swiperAutoplay || useFixedText;
                },
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            toRemove: ['buttonColor', 'buttonTextColor'],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        amenitiesMessage: 'Nosso hotel possui:',
      },
      en: {
        amenitiesMessage: 'Our hotel has:',
      },
      es: {
        amenitiesMessage: 'Nuestro hotel cuenta con:',
      },
    },
  },
};
</script>

<style scoped lang="scss">
.amenities-slider {
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  overflow: hidden;

  &.container {
    max-width: 100%;
  }

  .content {
    max-width: 1600px;
    padding-left: 16vw;
    padding-right: 16vw;

    .swiper {
      overflow: initial;
      margin-left: -12vw;
      margin-right: -12vw;

      .swiper-wrapper {
        .swiper-slide {
          display: flex;
          flex-direction: column;
          margin: auto 0;
          height: 450px;
          padding: 40px;
          border-radius: 8px;
          transition: padding 0.4s linear;

          .img-wrapper {
            width: 100%;
            height: 100%;
            border-radius: 16px;
            overflow: hidden;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
              opacity: 0.8;
            }
          }

          &.swiper-slide-active {
            padding: 0;
            transition-delay: 0.1s;

            .img-wrapper {
              img {
                opacity: 1;
              }
            }
          }
        }
      }

      @include media-breakpoint-down(sm) {
        margin: 0;
      }

      .swiper-button-wrapper {
        display: flex;
        justify-content: space-between;
        width: 160px;
        margin: 0 auto;

        @include media-breakpoint-down(lg) {
          height: 100px;
        }

        .swiper-button-prev,
        .swiper-button-next {
          position: absolute;
          width: 48px;
          height: 48px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 100px;

          &.swiper-button-disabled {
            display: none;
          }

          &::after {
            background: var(--slide-bg-color, $lb-white);
            box-shadow: 0px 10px 16px rgba(0, 0, 0, 0.2);
            color: var(--slide-text-color, $gray-900);
            border-radius: 100px;
            width: 48px;
            height: 48px;
            font-size: 18px;
            display: flex;
            justify-content: center;
            align-items: center;
          }

          @include media-breakpoint-down(lg) {
            position: relative;

            &.swiper-button-disabled {
              display: block;
            }
          }
        }
      }
    }

    .txt-bottom {
      text-align: center;
      margin-top: 32px;
    }

    .amenities {
      width: 100%;
      margin-top: 32px;
      display: flex;
      align-items: left;

      @include media-breakpoint-down(lg) {
        flex-direction: column;
        gap: 20px;
      }

      .amenities-message {
        white-space: nowrap;
        font-weight: bold;
        text-align: left;
        margin-right: 20px;
      }

      .amenities-wrapper {
        width: 100%;
        text-align: left;
        line-height: 1.8;

        .amenities-item {
          margin-right: 20px;
        }
      }

      .loader {
        margin: 0 auto;
      }
    }

    @include media-breakpoint-down(sm) {
      padding: 0;
    }
  }

  @include media-breakpoint-down(sm) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }
}
</style>
